import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type QueryState = 'idle' | 'analyzing' | 'generating' | 'executing' | 'completed' | 'error';
export type NavigationState = 'idle' | 'loading' | 'error';

interface LoadingStates {
  // Global navigation
  tabNavigation: NavigationState;
  currentTab: string;

  // Query processing
  queryState: QueryState;
  queryProgress: number; // 0-100
  queryMessage: string;

  // General loading states
  globalLoading: boolean;
  networkStatus: 'online' | 'offline' | 'checking';
}

interface AppStateContextType extends LoadingStates {
  // Navigation actions
  setTabNavigation: (state: NavigationState, tab?: string) => void;
  startTabTransition: (tab: string) => void;
  completeTabTransition: () => void;
  errorTabTransition: (error: string) => void;

  // Query state actions
  setQueryState: (state: QueryState, message?: string, progress?: number) => void;
  startQuery: (message?: string) => void;
  updateQueryProgress: (state: QueryState, message?: string, progress?: number) => void;
  completeQuery: () => void;
  errorQuery: (error: string) => void;

  // General actions
  setGlobalLoading: (loading: boolean) => void;
  setNetworkStatus: (status: 'online' | 'offline' | 'checking') => void;

  // Utility actions
  reset: () => void;
}

const initialState: LoadingStates = {
  tabNavigation: 'idle',
  currentTab: '',
  queryState: 'idle',
  queryProgress: 0,
  queryMessage: '',
  globalLoading: false,
  networkStatus: 'online',
};

const AppStateContext = createContext<AppStateContextType | undefined>(undefined);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LoadingStates>(initialState);

  // Navigation actions
  const setTabNavigation = useCallback((navState: NavigationState, tab?: string) => {
    setState(prev => ({
      ...prev,
      tabNavigation: navState,
      currentTab: tab || prev.currentTab
    }));
  }, []);

  const startTabTransition = useCallback((tab: string) => {
    setTabNavigation('loading', tab);
  }, [setTabNavigation]);

  const completeTabTransition = useCallback(() => {
    setTabNavigation('idle');
  }, [setTabNavigation]);

  const errorTabTransition = useCallback((error: string) => {
    console.error('Tab transition error:', error);
    setTabNavigation('error');
  }, [setTabNavigation]);

  // Query state actions
  const setQueryState = useCallback((queryState: QueryState, message = '', progress = 0) => {
    setState(prev => ({
      ...prev,
      queryState,
      queryMessage: message,
      queryProgress: Math.max(0, Math.min(100, progress))
    }));
  }, []);

  const startQuery = useCallback((message = 'Analyzing your query...') => {
    setQueryState('analyzing', message, 10);
  }, [setQueryState]);

  const updateQueryProgress = useCallback((
    queryState: QueryState,
    message = '',
    progress = 0
  ) => {
    setQueryState(queryState, message, progress);
  }, [setQueryState]);

  const completeQuery = useCallback(() => {
    setQueryState('completed', 'Query completed successfully', 100);

    // Auto-reset after 2 seconds
    setTimeout(() => {
      setQueryState('idle', '', 0);
    }, 2000);
  }, [setQueryState]);

  const errorQuery = useCallback((error: string) => {
    setQueryState('error', error, 0);

    // Auto-reset after 5 seconds
    setTimeout(() => {
      setQueryState('idle', '', 0);
    }, 5000);
  }, [setQueryState]);

  // General actions
  const setGlobalLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, globalLoading: loading }));
  }, []);

  const setNetworkStatus = useCallback((networkStatus: 'online' | 'offline' | 'checking') => {
    setState(prev => ({ ...prev, networkStatus }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const value: AppStateContextType = React.useMemo(() => ({
    ...state,
    setTabNavigation,
    startTabTransition,
    completeTabTransition,
    errorTabTransition,
    setQueryState,
    startQuery,
    updateQueryProgress,
    completeQuery,
    errorQuery,
    setGlobalLoading,
    setNetworkStatus,
    reset,
  }), [
    state,
    setTabNavigation,
    startTabTransition,
    completeTabTransition,
    errorTabTransition,
    setQueryState,
    startQuery,
    updateQueryProgress,
    completeQuery,
    errorQuery,
    setGlobalLoading,
    setNetworkStatus,
    reset,
  ]);

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
}

// Utility hook for query step management
export function useQuerySteps() {
  const { queryState, updateQueryProgress } = useAppState();

  const steps = {
    analyze: () => updateQueryProgress('analyzing', 'Analyzing your query...', 20),
    generate: () => updateQueryProgress('generating', 'Generating SQL query...', 60),
    execute: () => updateQueryProgress('executing', 'Executing query...', 90),
    complete: () => updateQueryProgress('completed', 'Query completed', 100),
  };

  return { queryState, steps };
}

// Utility hook for tab navigation management
export function useTabNavigation() {
  const {
    tabNavigation,
    currentTab,
    startTabTransition,
    completeTabTransition,
    errorTabTransition
  } = useAppState();

  const navigateToTab = useCallback(async (
    tab: string,
    loadFunction?: () => Promise<void>
  ) => {
    startTabTransition(tab);

    try {
      if (loadFunction) {
        await loadFunction();
      }
      completeTabTransition();
    } catch (error: any) {
      errorTabTransition(error.message || 'Navigation failed');
    }
  }, [startTabTransition, completeTabTransition, errorTabTransition]);

  return {
    tabNavigation,
    currentTab,
    navigateToTab,
    isNavigating: tabNavigation === 'loading',
    hasNavigationError: tabNavigation === 'error'
  };
}